

import { useState, useEffect, useRef } from "react"
import TypewriterRendererPlain from "./TypeWriterResponsePlain"
import axios from "axios"

// Helper function to create a safe hash for any string
const createSafeKey = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return Math.abs(hash).toString(36).substring(0, 10)
}

// Copy to clipboard function with feedback
const copyToClipboard = async (text, setCopyStatus) => {
  try {
    await navigator.clipboard.writeText(text)
    setCopyStatus("copied")
    setTimeout(() => setCopyStatus(""), 2000)
  } catch (err) {
    console.error("Failed to copy: ", err)
    setCopyStatus("error")
    setTimeout(() => setCopyStatus(""), 2000)
  }
}

// Function to check if a language is a programming language
const isProgrammingLanguage = (lang) => {
  const programmingLanguages = [
    "code",
    "python",
    "javascript",
    "typescript",
    "java",
    "c",
    "cpp",
    "c++",
    "csharp",
    "c#",
    "go",
    "rust",
    "swift",
    "kotlin",
    "php",
    "ruby",
    "perl",
    "scala",
    "haskell",
    "lua",
    "r",
    "dart",
    "bash",
    "shell",
    "sql",
    "html",
    "css",
    "xml",
    "json",
    "yaml",
  ]
  return programmingLanguages.includes(lang.toLowerCase())
}

// Add this function at the top of the file, after the existing helper functions
// Function to sanitize Mermaid diagram code
const sanitizeMermaidCode = (code) => {
  return (
    code
      .replace(/<br\/>/g, "<br>") // Fix line breaks
      .replace(/\s+-/g, " -") // Fix spacing before dashes
      .replace(/-\s+/g, "- ") // Fix spacing after dashes

      // Handle nested brackets in node labels
      .replace(/\[([^\]]*)\[([^\]]*)\]([^\]]*)\]/g, (match, before, middle, after) => {
        return `[${before}(${middle})${after}]`
      })

      // Replace mathematical symbols with plain text alternatives
      .replace(/⌊/g, "(") // Replace floor start with parenthesis
      .replace(/⌋/g, ")") // Replace floor end with parenthesis
      .replace(/≤/g, "<=") // Replace less than or equal
      .replace(/≥/g, ">=") // Replace greater than or equal
      .replace(/÷/g, "/") // Replace division symbol
      .replace(/×/g, "*") // Replace multiplication symbol
      .replace(/√/g, "sqrt") // Replace square root
      .replace(/∑/g, "sum") // Replace summation
      .replace(/∫/g, "integral") // Replace integral
      .replace(/π/g, "pi") // Replace pi
      .replace(/θ/g, "theta") // Replace theta
      .replace(/α/g, "alpha") // Replace alpha
      .replace(/β/g, "beta") // Replace beta
      .replace(/γ/g, "gamma") // Replace gamma
      .replace(/δ/g, "delta") // Replace delta
      .replace(/ε/g, "epsilon") // Replace epsilon
      .replace(/λ/g, "lambda") // Replace lambda
      .replace(/μ/g, "mu") // Replace mu
      .replace(/σ/g, "sigma") // Replace sigma
      .replace(/τ/g, "tau") // Replace tau
      .replace(/ω/g, "omega") // Replace omega
      .replace(/∞/g, "infinity") // Replace infinity

      // Replace other potentially problematic characters
      .replace(/[^\x00-\x7F]/g, (char) => {
        return `&#${char.charCodeAt(0)};`
      })
      .trim()
  )
}

// Add this function after the sanitizeMermaidCode function and before formatResponse
const formatListItems = (text) => {
  if (!text) return ""

  // Replace lines starting with dash with proper list items
  return text.replace(/^(\s*)-\s+(.+)$/gm, "$1• $2")
}

// Add this new function after the formatListItems function and before formatResponse
const enhanceListFormatting = (content) => {
  if (!content) return content

  // First pass: Only identify list items with dashes that have content
  const enhancedContent = content.replace(/^(\s*)-\s+(.+)$/gm, (match, indent, text) => {
    if (text.trim()) {
      // Only process if there's actual content after the dash
      return `${indent}• ${text}`
    }
    return match // Keep the original if there's no content
  })

  // Second pass: Convert consecutive bullet points to numbered items with better styling
  const lines = enhancedContent.split("\n")
  let inList = false
  let listItemCount = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const isListItem = line.match(/^\s*•\s+(.+)$/) // Only match bullets with content

    if (isListItem && isListItem[1].trim()) {
      // Check if there's content after the bullet
      if (!inList) {
        // Start of a new list
        inList = true
        listItemCount = 1
      } else {
        // Continuing a list
        listItemCount++
      }

      // Replace bullet with numbered square for consecutive items
      if (listItemCount > 1) {
        lines[i] = line.replace(/^\s*•\s+/, (match) => {
          const indentation = match.match(/^\s*/)[0]
          return `\\(\\quad\\)\\textbf{${listItemCount}}\\(\\quad\\)`
        })
      } else {
        // Keep the first item as a bullet but with enhanced styling
        lines[i] = line.replace(/^\s*•\s+/, (match) => {
          const indentation = match.match(/^\s*/)[0]
          return `\\(\\quad\\)\\textbf{1}\\(\\quad\\\)`
        })
      }
    } else if (line.trim() === "") {
      // Empty line ends the list
      inList = false
    }
  }

  return lines.join("\n")
}

// Function for wraping lines in delimiters
function wrapLatexLines(src) {
  const beginRE = /^\s*\\begin\{|\\text\{([^}]+)\}/;
  const alreadyMathRE = /^\s*(\\\(|\\\[|\$)/;
  const separatorRE = /^\s*-{3,}\s*$/;
  const commentRE = /^\s*%/;
  const htmlTagRE = /^\s*<[^>]+>/;
  const markdownHeaderRE = /^\s*#{1,6}\s/;
  
  // Detect LaTeX commands and symbols
  const latexCommandRE = /\\[a-zA-Z]+/;
  const latexSymbolRE = /[\{\}\_\^]/;
  const inlineMathRE = /\$[^$]+\$|\\\([^$]+\\\)$/g;
  const hasLatexRE = /\\[a-zA-Z]+|[\{\}\_\^\\]|\$[^$]*\$|\\\([^$]*\\\)|\\[$$$$\[\\]/g;

  const lines = src.split("\n");
  const out = [];
  let buf = [];
  let openEnv = null;
  
  const flush = () => {
    if (buf.length) {
      out.push(`\$${buf.join("\n")}\$`);
      buf.length = 0;
    }
  };
  
  // Check if line contains mixed content (both LaTeX and regular text)
  const hasMixedContent = (line) => {
    const trimmed = line.trim();
    
    // If it's already wrapped math, not mixed
    if (alreadyMathRE.test(trimmed)) return false;
    
    // If it has inline math delimiters, it's mixed
    if (inlineMathRE.test(trimmed)) return true;
    
    // Check if it has LaTeX commands but also regular words
    const hasLatex = hasLatexRE.test(trimmed);
    const hasRegularWords = /\b[a-zA-Z]{2,}\b/.test(trimmed.replace(/\\[a-zA-Z]+/g, ''));
    
    return hasLatex && hasRegularWords;
  };
  
  // Process mixed content line
  const processMixedLine = (line) => {
    // If already has inline math delimiters, leave as-is
    if (inlineMathRE.test(line)) {
      return line;
    }
    
    // Split by words and process each part
    const parts = line.split(/(\s+)/);
    const processedParts = parts.map(part => {
      const trimmedPart = part.trim();
      if (!trimmedPart || /^\s+$/.test(part)) {
        return part; // preserve whitespace
      }

      // If this part contains LaTeX commands/symbols, wrap it
      if (hasLatexRE.test(trimmedPart)) {
        return `\\\(${trimmedPart}\\\)`;
      }
      
      return part;
    });
    
    return processedParts.join('');
  };
  
  for (const line of lines) {
    const trimmed = line.trim();

    // 0) blank → just keep it
    if (!trimmed) {
      flush();
      out.push(line);
      continue;
    }

    // 1) inside an environment?
    if (openEnv) {
      buf.push(line);
      const endRE = new RegExp(`^\\s*\\\\end\\{${openEnv}\\}`);
      if (endRE.test(line)) {
        flush();
        openEnv = null;
      }
      continue;
    }
    
    // 2) separator lines → keep as-is
    if (separatorRE.test(line)) {
      flush();
      out.push(line);
      continue;
    }
    
    // 3) LaTeX comments → keep as-is
    if (commentRE.test(line)) {
      flush();
      out.push(line);
      continue;
    }
    
    // 4) HTML tags → keep as-is
    if (htmlTagRE.test(line)) {
      flush();
      out.push(line);
      continue;
    }
    
    // 5) Markdown headers → keep as-is
    if (markdownHeaderRE.test(line)) {
      flush();
      out.push(line);
      continue;
    }
    
    // 6) already math? leave it
    if (alreadyMathRE.test(line)) {
      out.push(line);
      continue;
    }

    // 3) start of an environment?  buffer it
    const m = beginRE.exec(line);
    if (m) {
      openEnv = m[1];
      buf.push(line);
      continue;
    }
    
    // 8) mixed content (LaTeX + regular text)?
    if (hasMixedContent(line)) {
      flush();
      out.push(processMixedLine(line));
      continue;
    }

    // 9) ends with \ → strip break, wrap, emit
    if (/\\\s*$/.test(line)) {
      const body = line.replace(/\\\\\s*$/, "");
      out.push(`\\\(${body.trim()}\\\)`);
      continue;
    }

    // 10) pure LaTeX line → wrap entirely
    if (hasLatexRE.test(line)) {
      out.push(`\\\(${line.trim()}\\\)`);
      continue;
    }
    
    // 11) regular text line → keep as-is
    out.push(line);
  }
  
  flush();
  return out.join("\n");
}
// Add these new helper functions after the existing helper functions and before the formatResponse function

// Function to export content as text file
const exportAsText = (content) => {
  const element = document.createElement("a")
  const file = new Blob([content], { type: "text/plain" })
  element.href = URL.createObjectURL(file)
  element.download = `ai-response-${new Date().toISOString().slice(0, 10)}.txt`
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)
}

// Function to export content as HTML/PDF
const exportAsHTML = (containerRef, type = "html") => {
  if (!containerRef.current) return

  // Get the HTML content
  const content = containerRef.current.innerHTML

  // Create a new HTML document with proper styling
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>AI Response</title>
        <style>
          body {
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
            line-height: 1.6;
            color: #1a202c;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          pre, code {
            font-family: 'Courier New', monospace;
            background-color: #f7fafc;
            padding: 0.2rem 0.4rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
            color: #2d3748;
          }
          pre {
            padding: 1rem;
            overflow-x: auto;
          }
          h1, h2, h3, h4 {
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 1rem;
            color: #2d3748;
          }
          h1 {
            font-size: 1.875rem;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 0.5rem;
          }
          h2 { font-size: 1.5rem; }
          h3 { font-size: 1.25rem; }
          ul, ol { padding-left: 1.5rem; margin: 1rem 0; }
          li { margin-bottom: 0.5rem; }
          a { color: #3182ce; }
          blockquote {
            border-left: 4px solid #e2e8f0;
            padding-left: 1rem;
            color: #4a5568;
            font-style: italic;
            margin: 1.5rem 0;
          }
          img { max-width: 100%; }
          .list-bullet, .list-number {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            background-color: #333;
            color: white;
            border-radius: 4px;
            font-size: 14px;
            margin-right: 8px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `

  if (type === "html") {
    // Export as HTML file
    const element = document.createElement("a")
    const file = new Blob([html], { type: "text/html" })
    element.href = URL.createObjectURL(file)
    element.download = `ai-response-${new Date().toISOString().slice(0, 10)}.html`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  } else if (type === "pdf" && window.print) {
    // For PDF, we'll use the browser's print functionality
    // Create a new window and write the HTML content
    const printWindow = window.open("", "_blank")
    printWindow.document.write(html)
    printWindow.document.close()

    // Wait for resources to load then print
    printWindow.onload = () => {
      printWindow.focus()
      printWindow.print()
      // Don't close the window immediately to allow the print dialog to appear
      setTimeout(() => printWindow.close(), 500)
    }
  }
}

// Function to export as image using html2canvas
const exportAsImage = async (containerRef) => {
  if (!containerRef.current) return

  try {
    // Dynamically import html2canvas
    const html2canvasModule = await import("html2canvas")
    const html2canvas = html2canvasModule.default

    const canvas = await html2canvas(containerRef.current, {
      scale: 2, // Higher scale for better quality
      useCORS: true, // To handle images from different origins
      logging: false,
      backgroundColor: "#ffffff",
    })

    // Convert to image and download
    const image = canvas.toDataURL("image/png")
    const element = document.createElement("a")
    element.href = image
    element.download = `ai-response-${new Date().toISOString().slice(0, 10)}.png`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  } catch (error) {
    console.error("Error exporting as image:", error)
    alert("Failed to export as image. Please try another format.")
  }
}

// Function to export as Word document (DOCX)
const exportAsWord = async (containerRef) => {
  if (!containerRef.current) return

  try {
    // Get the HTML content
    const content = containerRef.current.innerHTML

    // Create a Blob with the HTML content
    const preHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>AI Response</title>
        <style>
          body { font-family: 'Calibri', sans-serif; }
          pre, code { font-family: 'Courier New', monospace; background-color: #f5f5f5; padding: 5px; }
        </style>
      </head>
      <body>`
    const postHtml = "</body></html>"
    const html = preHtml + content + postHtml

    const blob = new Blob(["\ufeff", html], {
      type: "application/msword",
    })

    // Create download link
    const element = document.createElement("a")
    element.href = URL.createObjectURL(blob)
    element.download = `ai-response-${new Date().toISOString().slice(0, 10)}.doc`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  } catch (error) {
    console.error("Error exporting as Word document:", error)
    alert("Failed to export as Word document. Please try another format.")
  }
}

// Update the formatResponse function to use the new enhanceListFormatting function
const formatResponse = (
  text,
  onGenerateImage,
  generatedImages,
  isLoading,
  visibleImages,
  toggleImageVisibility,
  copyStatus,
  setCopyStatus,
  useTypewriter = true,
) => {
  // Check for undefined or empty content
  if (!text) {
    console.warn("formatResponse received undefined or empty text")
    return [
      <div key="error-message" className="text-yellow-600 p-4 border border-yellow-300 bg-yellow-50 rounded-md">
        <h3 className="font-medium">Content Unavailable</h3>
        <p className="text-gray-700">
          The requested content could not be displayed. This might be because the practice problems are being handled by
          a different component.
        </p>
      </div>,
    ]
  }

  // Apply enhanced list formatting
  text = enhanceListFormatting(text)

  // Regex to detect code blocks
  const codeBlockRegex =
    /```(python|mermaid|Step1|Step2|Step3|Step4|Step5|Step6|Step7|Step8|Step9|Step10|Step 1|Step 2|Step 3|Step 4|Step 5|Step 6|Step 7|Step 8|Step 9|Step 10|STEP1|STEP2|STEP3|STEP4|STEP5|STEP6|STEP7|STEP8|STEP9|STEP10|Case1|Case2|Case3|Case4|Case5|Case6|Case7|Case8|Case9|Case10|Case 1|Case 2|Case 3|Case 4|Case 5|Case 6|Case 7|Case 8|Case 9|Case 10|latex|javascript|typescript|java|c|cpp|c\+\+|csharp|c#|go|rust|swift|kotlin|php|ruby|perl|scala|haskell|lua|r|dart|bash|shell|sql|html|css|xml|json|yaml)?([\s\S]*?)```/g
  const parts = []
  let lastIndex = 0
  let match

  while ((match = codeBlockRegex.exec(text)) !== null) {
    const lang = match[1] || "" // Get language or default to "code"
    const code = match[2].trim() // Extract the code content
    const index = match.index

    // Push normal text before the code block into TypewriterResponse
    if (index > lastIndex) {
      const content = formatListItems(text.substring(lastIndex, index)).trim();
      if (/\S/.test(content)) { // Check if contains non-whitespace characters
        parts.push(
          <TypewriterRendererPlain
            key={`text-${lastIndex}`}
            content={content}
            useTypewriter={useTypewriter}
          />,
        );
      }
    }

    // Check if this is Python code that might generate a plot
    const isPythonPlot =
      lang.toLowerCase() === "python" &&
      (code.includes("matplotlib") ||
        code.includes("plt.") ||
        code.includes("seaborn") ||
        code.includes("sns.") ||
        code.includes("plotly") ||
        code.includes("px."))

    // Check if this is a mermaid diagram
    const isMermaid = lang.toLowerCase() === "mermaid"

    // Check if this is LaTeX code
    const isLatex = lang.toLowerCase() === "latex"

    // Check if this is a programming language
    const isProgramming = isProgrammingLanguage(lang)

    // Create a unique key for this code block using our safe function
    const codeKey = createSafeKey(code)

    if (isPythonPlot) {
      // For Python plotting code, replace with button or image
      if (isLoading[codeKey]) {
        // Cooler loading animation
        parts.push(
          <div
            key={`code-${index}`}
            className="my-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md flex flex-col items-center justify-center transition-all duration-300 transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group"
          >
            <div className="relative w-16 h-16 mb-3">
              {/* Animated circles */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-blue-200 opacity-75 animate-ping"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full border-4 border-blue-300 border-t-transparent animate-spin"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-blue-500 animate-pulse"></div>
              </div>
            </div>
            <p className="text-gray-700 font-medium text-center">Creating your visualization...</p>
          </div>,
        )
      } else if (visibleImages[codeKey] && generatedImages[codeKey]) {
        // Show the image with control buttons
        parts.push(
          <div
            key={`code-${index}`}
            className="my-6 bg-gradient-to-r from-gray-900 to-gray-800 overflow-hidden rounded-lg shadow-lg transition-all duration-300 transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group"
          >
            <div className="relative p-6">
              <img
                src={generatedImages[codeKey] || "/placeholder.svg"}
                alt="AI-generated visualization"
                className="w-full object-contain max-h-[600px]"
              />

              {/* Control buttons overlay - Fixed tooltip positioning */}
              <div className="absolute top-2 right-2 flex space-x-3">
                <div className="relative group">
                  <button
                    onClick={() => toggleImageVisibility(codeKey)}
                    className="flex p-2 bg-gray-600 bg-opacity-70 hover:bg-opacity-90 text-white rounded-full shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    title="Hide image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                        clipRule="evenodd"
                      />
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                    </svg>
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                    Hide image
                  </div>
                </div>

                <div className="relative group">
                  <a
                    href={generatedImages[codeKey]}
                    download={`visualization-${new Date().getTime()}.png`}
                    className="flex p-2 bg-gray-600 bg-opacity-70 hover:bg-opacity-90 text-white rounded-full shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    title="Download image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </a>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                    Download
                  </div>
                </div>

                <div className="relative group">
                  <button
                    onClick={() => copyToClipboard(code, setCopyStatus)}
                    className="flex p-2 bg-gray-600 bg-opacity-70 hover:bg-opacity-90 text-white rounded-full shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    title="Copy code"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                    {copyStatus === "copied" ? "Copied!" : "Copy code"}
                  </div>
                </div>
              </div>

              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <p className="text-white text-sm font-medium">Generated visualization</p>
              </div>
            </div>
          </div>,
        )
      } else {
        // Compact Show Image button
        parts.push(
            <button
            key={`code-${index}`}
            onClick={() => onGenerateImage(code, codeKey)}
            className="my-4 inline-flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-green-400 border border-green-500 rounded-sm font-mono text-sm shadow-md transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-green-500 focus:ring-offset-1 mx-auto group relative overflow-hidden transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800"></div>
            <div className="absolute inset-0 opacity-10 bg-grid-white-100"></div>
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500 to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500 to-transparent"></div>

            <div className="relative flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="font-mono tracking-wide">
                <span className="text-white opacity-70">[</span>
                <span>Show Image Illustration</span>
                <span className="text-white opacity-70">]</span>
              </span>
            </div>

            {/* Terminal-like cursor animation */}
            <div className="absolute right-2 h-4 w-1 bg-green-400 opacity-75 animate-pulse"></div>
          </button>,
        )
      }
    } else if (isMermaid) {
      // For mermaid diagrams, create a unique container
      const diagramId = `mermaid-diagram-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`

      // Store the original code in a data attribute to avoid parsing issues
      const cleanedCode = sanitizeMermaidCode(code)

      parts.push(
        <div
          key={`code-${index}`}
          className="my-4 overflow-hidden rounded-lg shadow-lg bg-white border border-gray-200 transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group"
        >
          <div className="flex justify-between items-center bg-gray-50 px-4 py-2 border-b border-gray-200">
            <p className="text-gray-500 text-xs font-semibold">MERMAID DIAGRAM</p>
            <div className="flex space-x-2">
              <div className="relative group">
                <button
                  onClick={() => copyToClipboard(code, setCopyStatus)}
                  className={`p-1.5 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 ${copyStatus === "copied" ? "text-green-500" : "text-gray-500 hover:text-gray-700"
                    }`}
                  title="Copy code"
                >
                  {copyStatus === "copied" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  )}
                </button>
                <div className="absolute bottom-full right-0 transform -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                  {copyStatus === "copied" ? "Copied!" : "Copy diagram code"}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-b-lg">
            <div className="mermaid-container">
              {/* Store the original code in a hidden pre element */}
              <pre id={`source-${diagramId}`} style={{ display: "none" }}>
                {cleanedCode}
              </pre>

              {/* Create an empty div for rendering the diagram */}
              <div id={diagramId} className="mermaid-render-target" data-source-id={`source-${diagramId}`}></div>
            </div>
          </div>
        </div>,
      )
    } else if (isLatex) {
      // For LaTeX code blocks, display with special styling and buttons
      parts.push(
        <div
          key={`code-${index}`}
          className="my-4 overflow-hidden rounded-lg shadow-lg bg-white border border-gray-200 "
        >
          <div className="flex justify-between items-center bg-gray-50 px-4 py-2 border-b border-gray-200 transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group">
            <p className="text-gray-500 text-xs font-semibold">LATEX</p>
            <div className="flex space-x-2">
              <div className="relative group">
                <button
                  onClick={() => copyToClipboard(code, setCopyStatus)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  title="Copy LaTeX"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                </button>
                <div className="absolute bottom-full right-0 transform -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                  {copyStatus === "copied" ? "Copied!" : "Copy LaTeX"}
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50">
            <pre className="bg-white text-gray-800 p-4 rounded-md whitespace-pre-wrap max-w-full overflow-x-auto border border-gray-200">
              <TypewriterRendererPlain content={code} useTypewriter={useTypewriter} />
            </pre>
          </div>
        </div>,
      )
    } else if (isProgramming) {
      // For programming language code blocks, display with syntax highlighting and copy button
      // But DON't use TypewriterRendererPlain to preserve code font styling
      parts.push(
        <div
          key={`code-${index}`}
          className="my-4 overflow-hidden rounded-lg shadow-lg bg-white border border-gray-200 transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group"
        >
          <div className="flex justify-between items-center bg-gray-50 px-4 py-2 border-b border-gray-200">
            <p className="text-gray-500 text-xs font-semibold">{lang.toUpperCase() || "CODE"}</p>
            <div className="flex space-x-2">
              <div className="relative group">
                <button
                  onClick={() => copyToClipboard(code, setCopyStatus)}
                  className={`p-1.5 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 ${copyStatus === "copied" ? "text-green-500" : "text-gray-500 hover:text-gray-700"
                    }`}
                  title="Copy code"
                >
                  {copyStatus === "copied" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  )}
                </button>
                <div className="absolute bottom-full right-0 transform -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                  {copyStatus === "copied" ? "Copied!" : "Copy code"}
                </div>
              </div>
            </div>
          </div>
          <pre className="bg-gray-50 text-gray-800 p-4 rounded-md whitespace-pre-wrap max-w-full overflow-x-auto">
            <code className="font-mono text-sm">{code}</code>
          </pre>
        </div>,
      )
    } else {
      // For other code blocks, display with syntax highlighting and copy button
      parts.push(
        <div
          key={`code-${index}`}
          className="my-4 overflow-hidden rounded-lg shadow-lg bg-white border border-gray-200 transform hover:shadow-xl hover:scale-[1.01] hover:border hover:border-blue-300 group"
        >
          <div className="flex justify-between items-center bg-gray-200 px-4 py-0 border-b border-gray-200">
            <p className="text-gray-500 text-xs font-semibold">{lang.toUpperCase() || "CODE"}</p>
            <div className="flex space-x-2">
              <div className="relative group">
                <button
                  onClick={() => copyToClipboard(code, setCopyStatus)}
                  className={`p-1.5 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 ${copyStatus === "copied" ? "text-green-500" : "text-gray-500 hover:text-gray-700"
                    }`}
                  title="Copy code"
                >
                  {copyStatus === "copied" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  )}
                </button>
                <div className="absolute bottom-full right-0 transform -translate-y-2 z-10 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                  {copyStatus === "copied" ? "Copied!" : "Copy code"}
                </div>
              </div>
            </div>
          </div>
          <pre className="bg-white bg-opacity-0 text-gray-800 border-white border-opacity-100 py-0 px-16 min-w-[40vw] max-w-[60vw] overflow-x-auto">
            <code className="bg-white bg-opacity-0 text-sm">
              <TypewriterRendererPlain content={wrapLatexLines(code)} useTypewriter={useTypewriter} />
            </code>
          </pre>
        </div>,
      )
    }

    lastIndex = index + match[0].length
  }

  // Push remaining text after the last code block into TypewriterResponse
  if (lastIndex < text.length) {
    parts.push(
      <div key={`text-${lastIndex}`} className="prose prose-slate max-w-none">
        <TypewriterRendererPlain content={formatListItems(text.substring(lastIndex))
          .replace("Hint", "💡\\(\\large\\textbf{Hint}\\)").replace("Summary", "📊\\(\\large\\textbf{Summary}\\)")

        } useTypewriter={useTypewriter} />
      </div>,
    )
  }

  return parts
}

// Update the AIResponse component to accept and pass the useTypewriter prop
const AIResponse = ({ content, useTypewriter = true }) => {
  const [generatedImages, setGeneratedImages] = useState({})
  const [isLoading, setIsLoading] = useState({})
  const [visibleImages, setVisibleImages] = useState({})
  const [pythonCode, setPythonCode] = useState({})
  const [copyStatus, setCopyStatus] = useState("")
  const [fadeIn, setFadeIn] = useState(false)
  const [diagramIds, setDiagramIds] = useState([]) // Store diagram IDs
  const [showExportMenu, setShowExportMenu] = useState(false) // State for export menu
  const containerRef = useRef(null) // Reference to the container for export
  const exportMenuRef = useRef(null) // Reference to the export menu for click outside detection

  // Add click outside handler for export menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Add check for undefined content
/*   useEffect(() => {
    if (!content) {
      console.warn(
        "AIResponse received undefined content - this is expected for practice problems but may indicate an issue elsewhere",
      )
    }
  }, [content]) */

  // Add fade-in effect when component mounts
  useEffect(() => {
    setFadeIn(true)

    // Add Mermaid script if it doesn't exist
    if (!window.mermaid) {
      const script = document.createElement("script")
      script.src = "https://cdn.jsdelivr.net/npm/mermaid@10.6.1/dist/mermaid.min.js"
      script.async = true
      script.onload = () => {
        if (window.mermaid) {
          window.mermaid.initialize({
            startOnLoad: true,
            theme: "default",
            securityLevel: "loose",
            fontFamily: "Segoe UI, sans-serif",
            errorLabelColor: "transparent", // Hide error labels
            displayMode: false,
            logLevel: 1, // Reduce logging
            deterministicIds: true, // More stable rendering
            flowchart: {
              htmlLabels: true,
              curve: "basis",
            },
          })
        }
      }
      document.head.appendChild(script)
    }
  }, [])

  // Add a useEffect to initialize Mermaid diagrams after rendering
  useEffect(() => {
    // Wait for the DOM to update
    const timer = setTimeout(() => {
      // Find all mermaid render targets
      const renderTargets = document.querySelectorAll(".mermaid-render-target")

      renderTargets.forEach((target) => {
        const sourceId = target.getAttribute("data-source-id")
        if (sourceId) {
          const sourceElement = document.getElementById(sourceId)
          if (sourceElement) {
            const diagramCode = sourceElement.textContent || ""

            // Only try to render if we have actual diagram code and mermaid is available
            if (diagramCode.trim() && window.mermaid) {
              try {
                // First try to validate the diagram
                const sanitizedCode = sanitizeMermaidCode(diagramCode)

                window.mermaid
                  .parse(sanitizedCode)
                  .then(() => {
                    // If parsing succeeds, render the diagram
                    return window.mermaid.render(`rendered-${target.id}`, sanitizedCode)
                  })
                  .then((result) => {
                    target.innerHTML = result.svg
                  })
                  .catch((error) => {
                    console.warn("Mermaid parse/render failed with sanitized code:", error)

                    // Try a more aggressive approach - simplify the diagram further
                    try {
                      // Create a simplified version by removing all complex expressions in node labels
                      const simplifiedCode = sanitizedCode
                        // Replace complex expressions in node labels with simpler text
                        .replace(/\[([^\]]+)=/g, "[") // Remove assignments in labels
                        .replace(/\[([^\]]+)$$([^)]+)$$([^\]]*)\]/g, "[$1$3]") // Remove parenthesized expressions

                      window.mermaid
                        .render(`rendered-${target.id}-simplified`, simplifiedCode)
                        .then((result) => {
                          target.innerHTML = result.svg
                        })
                        .catch((secondError) => {
                          console.warn("Simplified diagram also failed:", secondError)

                          // Last resort - try with minimal diagram structure
                          try {
                            // Extract just the basic graph structure without complex node labels
                            const basicStructure = sanitizedCode.replace(/\[([^\]]+)\]/g, "[Node]") // Replace all node labels with simple "Node"

                            window.mermaid
                              .render(`rendered-${target.id}-basic`, basicStructure)
                              .then((result) => {
                                target.innerHTML = result.svg
                              })
                              .catch((finalError) => {
                                // If all attempts fail, display the code with syntax highlighting
                                target.innerHTML = `<pre class="mermaid-code bg-gray-100 p-3 rounded-md overflow-x-auto">${diagramCode}</pre>`
                              })
                          } catch (finalAttemptError) {
                            // Display the code with syntax highlighting as fallback
                            target.innerHTML = `<pre class="mermaid-code bg-gray-100 p-3 rounded-md overflow-x-auto">${diagramCode}</pre>`
                          }
                        })
                    } catch (retryError) {
                      // Display the code with syntax highlighting as fallback
                      target.innerHTML = `<pre class="mermaid-code bg-gray-100 p-3 rounded-md overflow-x-auto">${diagramCode}</pre>`
                    }
                  })
              } catch (error) {
                console.warn("Mermaid API error during useEffect:", error)

                // Final fallback: just show the code
                target.innerHTML = `<pre class="mermaid-code bg-gray-100 p-3 rounded-md overflow-x-auto">${diagramCode}</pre>`
              }
            }
          }
        }
      })
    }, 1000) // Longer delay to ensure DOM is fully updated

    return () => clearTimeout(timer)
  }, [content]) // Re-run when content changes

  // Early return for undefined content
const [dots, setDots] = useState("")

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === "...") return ""
        return prev + "."
      })
    }, 500)

    return () => clearInterval(interval)
  }, [])

  if (!content) {
    return (
      <div className="p-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm">
        <div className="flex items-center space-x-3 mb-3">
          <div className="relative">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
          </div>
          <h3 className="text-blue-700 font-semibold text-lg">AI is thinking{dots}</h3>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
          </div>

          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded-full w-4/5 animate-pulse" style={{ animationDelay: "0.2s" }}></div>
            <div className="h-3 bg-gray-200 rounded-full w-3/5 animate-pulse" style={{ animationDelay: "0.4s" }}></div>
          </div>
        </div>

        <p className="text-gray-600 text-sm">Generating your personalized response...</p>
      </div>
    )
  }

  const generateImage = async (code, codeKey) => {
    // Store the Python code for reference
    setPythonCode((prev) => ({ ...prev, [codeKey]: code }))

    // If image is already generated and hidden, just show it again
    if (generatedImages[codeKey] && !visibleImages[codeKey]) {
      setVisibleImages((prev) => ({ ...prev, [codeKey]: true }))
      return
    }

    try {
      setIsLoading((prev) => ({ ...prev, [codeKey]: true }))

      // Call your API endpoint that handles the Python code execution
      const response = await axios.post("/api/python-image-generator", {
        code: code,
      })

      // Store the generated image
      setGeneratedImages((prev) => ({
        ...prev,
        [codeKey]: response.data.plotBase64,
      }))

      // Make the image visible
      setVisibleImages((prev) => ({ ...prev, [codeKey]: true }))
    } catch (error) {
      console.error("Error generating image:", error)
    } finally {
      setIsLoading((prev) => ({ ...prev, [codeKey]: false }))
    }
  }

  const toggleImageVisibility = (codeKey) => {
    setVisibleImages((prev) => ({
      ...prev,
      [codeKey]: !prev[codeKey],
    }))
  }

  return (
    <div className={`flex flex-col transition-opacity duration-500 ${fadeIn ? "opacity-100" : "opacity-0"}`}>
      {/* Export button and dropdown menu */}
      <div className="relative flex justify-start mb-4">
        <button
          onClick={() => setShowExportMenu(!showExportMenu)}
          className="flex items-center px-2 py-1 bg-gray-800 text-white rounded-full shadow-md hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Export Response as ...  
        </button>

        {showExportMenu && (
          <div
            ref={exportMenuRef}
            className="absolute top-full left-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200 overflow-hidden"
          >
            <div className="py-1">
              <button
                onClick={() => {
                  setShowExportMenu(false)
                  exportAsImage(containerRef)
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-3 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Save as Image (PNG)
              </button>

              <button
                onClick={() => {
                  setShowExportMenu(false)
                  exportAsText(content)
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-3 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Save as Text (.txt)
              </button>

              <button
                onClick={() => {
                  setShowExportMenu(false)
                  exportAsWord(containerRef)
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-3 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Save as Word (.doc)
              </button>

              <button
                onClick={() => {
                  setShowExportMenu(false)
                  exportAsHTML(containerRef)
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-3 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
                Save as HTML
              </button>

              <button
                onClick={() => {
                  setShowExportMenu(false)
                  exportAsHTML(containerRef, "pdf")
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-3 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Print/Save as PDF
              </button>
            </div>
          </div>
        )}
      </div>

      <div ref={containerRef} className="ai-response-container">
        {formatResponse(
          content,
          generateImage,
          generatedImages,
          isLoading,
          visibleImages,
          toggleImageVisibility,
          copyStatus,
          setCopyStatus,
          useTypewriter,
        )}
      </div>

      {/* Your existing styles */}
      <style jsx global>{`
        /* Smooth height transitions for the AI response container */
        .ai-response-container {
          /* Smooth height and content transitions */
          transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1),
                      min-height 0.6s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.3s ease-in-out;
          
          /* Prevent layout shifts during content changes */
          will-change: height, max-height, min-height;
          
          /* Set a reasonable max-height to enable smooth transitions */
          max-height: 5000px; /* Adjust based on your expected max content */
          
          /* Ensure smooth overflow handling */
          overflow: hidden;
          
          /* Enable hardware acceleration for smoother animations */
          transform: translateZ(0);
        }

        /* Smooth transitions for content within the container */
        .ai-response-container > * {
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Alternative approach using CSS Grid for even smoother height changes */
        .ai-response-container-grid {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .ai-response-container-grid.expanded {
          grid-template-rows: 1fr;
        }

        .ai-response-container-grid > div {
          min-height: 0;
          overflow: hidden;
        }

        /* Prevent content jumping during dynamic updates */
        .ai-response-container * {
          box-sizing: border-box;
        }

        /* Smooth transitions for images and media within responses */
        .ai-response-container img,
        .ai-response-container video,
        .ai-response-container canvas {
          transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
        }

        /* Export button styles */
        .export-button {
          transition: all 0.2s ease;
        }

        .export-button:hover {
          transform: translateY(-1px);
        }

        .export-menu-item {
          transition: background-color 0.15s ease;
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .ai-response-container,
          .ai-response-container > *,
          .ai-response-container-grid {
            transition: none;
          }
        }
      `}</style>
    </div>
  )
}

export default AIResponse

